/**
 * The reader's vocabulary and the comparison metrics.
 *
 * These describe a profile's *own declared configuration* — the orientation it was configured
 * with, resolved at generation time. Art Direction §14 (amendment): naming a profile's declared
 * position is describing the subject of the page, not the tool taking a side. What stays forbidden
 * is ranking, recommending, or ordering profiles by anything but their id.
 */

import type { EditorialOutput } from 'src/types/editorial';

export const ORIENTATION_LABEL: Record<string, string> = {
  left: 'Izquierda',
  center_left: 'Centro-izquierda',
  none: 'Sin orientación',
  center_right: 'Centro-derecha',
  right: 'Derecha',
};

/** Position on the declared spectrum, as a percentage. Describes; does not rank. */
export const ORIENTATION_POSITION: Record<string, number> = {
  left: 4,
  center_left: 27,
  none: 50,
  center_right: 73,
  right: 96,
};

export const ORIENTATION_COLOR: Record<string, string> = {
  left: '#8c1a3d',
  center_left: '#a84360',
  none: '#5a6472',
  center_right: '#3c6aa5',
  right: '#1b4b8f',
};

export const TREATMENT_LABEL: Record<string, string> = {
  informative: 'Informativo',
  explanatory: 'Explicativo',
  analytical: 'Analítico',
  critical: 'Crítico',
  moderate_sensational: 'Sensacionalista moderado',
};

export const INTENSITY_LABEL: Record<string, string> = {
  sober: 'sobrio',
  attractive: 'atractivo',
  emphatic: 'enfático',
};

export const OUTPUT_STATUS_LABEL: Record<string, string> = {
  pending_human_review: 'Lista para revisión',
  validation_failed: 'Bloqueada',
  blocked_eligibility: 'No elegible',
  generation_failed: 'Error de generación',
};

export const FINDING_LABEL: Record<string, string> = {
  unsupported: 'sin respaldo',
  contradicted: 'contradicho',
  partially_supported: 'respaldo parcial',
};

export type ReaderConfig = {
  key: string;
  label: string;
  color: string;
  position: number;
  treatment: string;
  intensity: string;
};

export function readerConfig(output: EditorialOutput): ReaderConfig {
  const config = output.payload.resolved_editorial_configuration ?? {};
  const rawLabel = config.orientation?.label ?? '';
  const key = ORIENTATION_LABEL[rawLabel] ? rawLabel : 'none';

  return {
    key,
    label: ORIENTATION_LABEL[key] ?? 'Sin orientación',
    color: ORIENTATION_COLOR[key] ?? ORIENTATION_COLOR['none']!,
    position: ORIENTATION_POSITION[key] ?? 50,
    treatment: (config.treatment && (TREATMENT_LABEL[config.treatment] ?? config.treatment)) || '',
    intensity:
      (config.expressive_intensity && (INTENSITY_LABEL[config.expressive_intensity] ?? config.expressive_intensity)) ||
      '',
  };
}

export type OutputMetrics = {
  words: number;
  claims: number;
  /** Words divided by the claims the note actually cited. Null when it cited none. */
  perClaim: number | null;
  cited: number;
  prose: number;
  /** Share of graded blocks the validator marked `supported`. Null when nothing was graded. */
  fidelity: number | null;
  blocking: number;
  revisions: number;
  seconds: number | null;
  model: string;
};

export function outputMetrics(output: EditorialOutput): OutputMetrics {
  const payload = output.payload;
  const blocks = payload.content?.body_blocks ?? [];
  const prose = blocks.filter(block => block.type !== 'heading');
  const words = blocks.reduce((total, block) => total + countWords(block.text), 0);

  const findings = payload.validation?.factual?.findings ?? [];
  const supported = findings.filter(finding => finding.classification === 'supported').length;
  const graded = findings.filter(finding => finding.classification !== undefined).length;

  // The claims a note used are its real evidence; without them "words per claim" says nothing.
  const claims = payload.traceability?.used_claim_ids?.length ?? 0;
  const durationMs = payload.generation?.duration_ms;

  return {
    words,
    claims,
    perClaim: claims > 0 ? Math.round(words / claims) : null,
    cited: prose.filter(block => (block.claim_ids ?? []).length > 0 || (block.fact_ids ?? []).length > 0).length,
    prose: prose.length,
    fidelity: graded > 0 ? Math.round((100 * supported) / graded) : null,
    blocking: payload.validation?.blocking_notes?.length ?? 0,
    revisions: payload.validation?.revision?.attempts ?? 0,
    seconds: durationMs === undefined ? null : Math.round(durationMs / 1000),
    model: payload.generation?.model || 'desconocido',
  };
}

function countWords(text: string | undefined): number {
  return String(text ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
}

/**
 * What stops this draft from being published, in the order an editor needs it.
 * Older outputs have no `blocking_notes`, so it is reconstructed from the raw findings.
 */
export function readerReview(output: EditorialOutput) {
  const validation = output.payload.validation ?? {};
  const deviations = (validation.editorial?.deviations ?? []).filter(deviation => deviation.severity !== 'ok');
  const findings = (validation.factual?.findings ?? []).filter(finding => finding.classification !== 'supported');
  const omitted = output.payload.traceability?.omitted_claims ?? [];

  const blocking: EditorialBlockingLike[] =
    validation.blocking_notes ??
    [
      ...findings.filter(finding => ['unsupported', 'contradicted'].includes(finding.classification)),
      ...deviations.filter(deviation => deviation.severity === 'error'),
    ];

  return {
    revision: validation.revision ?? {},
    blocking,
    findings,
    styleNotes: deviations.filter(deviation => deviation.severity !== 'error'),
    omitted,
    isEmpty:
      blocking.length === 0 &&
      findings.length === 0 &&
      deviations.filter(deviation => deviation.severity !== 'error').length === 0 &&
      omitted.length === 0 &&
      !validation.revision?.attempts,
  };
}

/** Findings and deviations share enough shape to render in one blocking list. */
export type EditorialBlockingLike = {
  problem?: string;
  classification?: string;
  field?: string;
  detail?: string;
};

// ── Newspaper furniture ──────────────────────────────────────────────────────
// A page that reads as a newspaper needs the pieces a newspaper has: a section above the
// headline, a place above the first line, an edition line. None of them are invented here —
// each one is a field the pipeline already recorded, and a page whose furniture is decoration
// would be the one thing Art Direction §4 forbids outright.

const CATEGORY_LABEL: Record<string, string> = {
  politics: 'Política',
  politica: 'Política',
  economy: 'Economía',
  economia: 'Economía',
  security: 'Seguridad',
  seguridad: 'Seguridad',
  health: 'Salud',
  salud: 'Salud',
  education: 'Educación',
  educacion: 'Educación',
  culture: 'Cultura',
  cultura: 'Cultura',
  environment: 'Medio ambiente',
  international: 'Internacional',
  society: 'Sociedad',
  infrastructure: 'Infraestructura',
  justice: 'Justicia',
  labor: 'Trabajo',
  technology: 'Tecnología',
  sports: 'Deportes',
  culture_entertainment: 'Cultura',
  security_justice: 'Seguridad',
  economy_finance: 'Economía',
  health_science: 'Salud',
  education_science: 'Educación',
  politics_government: 'Política',
  foreign_policy: 'Internacional',
  social_policy: 'Sociedad',
  energy: 'Energía',
  migration: 'Migración',
  other: 'General',
};

export type ReaderFurniture = {
  /** The section label above the headline. */
  kicker: string;
  /** The place the first line opens on, upper-cased as a dateline is set. */
  dateline: string;
  /** The topics the story was filed under, for the foot of the page. */
  topics: string[];
};

/**
 * The section, the place and the filing topics, from the prenota that produced this draft.
 *
 * Falls back to the story's own topics and then to «General» — a newspaper page always has a
 * section, and leaving the slot blank moves the headline up into the masthead rule.
 */
export function readerFurniture(prenota: { primary_category?: string; topics?: string[]; locations?: Array<{ name: string; level?: string; relationship?: string }> } | undefined): ReaderFurniture {
  const category = (prenota?.primary_category ?? '').toLowerCase();
  const topics = (prenota?.topics ?? []).filter(Boolean);
  const kicker = CATEGORY_LABEL[category] ?? (category ? capitalise(category) : topics[0] ? capitalise(topics[0]) : 'General');

  // The dateline is where the news happened, so a location the story is *about* outranks one it
  // merely mentions. Absent, the page opens without one rather than inventing a city.
  const locations = prenota?.locations ?? [];
  const primary = locations.find(location => location.relationship === 'primary') ?? locations[0];

  return { kicker, dateline: (primary?.name ?? '').toUpperCase(), topics };
}

/** A category the map does not know still has to read as a section, not as a database value. */
function capitalise(text: string): string {
  const words = text.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Art Direction §8: the word carries the meaning, the tint only groups. */
export const EPISTEMIC_LABEL: Record<string, string> = {
  verified_fact: 'Hecho documentado',
  official_statement: 'Postura oficial',
  original_reporting: 'Reporte periodístico',
  attributed_claim: 'Versión de una parte',
  attributed_unresolved: 'Atribuido, sin cerrar',
  editorial_inference: 'Lectura editorial',
  contradicted: 'Contradicho',
  insufficient_evidence: 'Sin soporte publicable',
};

export const EPISTEMIC_TONE: Record<string, string> = {
  verified_fact: 'supported',
  official_statement: 'unresolved',
  original_reporting: 'partial',
  attributed_claim: 'partial',
  attributed_unresolved: 'contradicted',
  editorial_inference: 'info',
  contradicted: 'contradicted',
  insufficient_evidence: 'contradicted',
};

export const RISK_LABEL: Record<string, string> = {
  low: '',
  medium: 'Riesgo medio',
  high: 'Riesgo alto',
  critical: 'Riesgo crítico',
};

/**
 * The gate codes that stopped a draft, in the words of the rule rather than of the code.
 *
 * An editor reading `validation_failed` cannot tell a dropped attribution from an invented
 * figure, and those need different decisions.
 */
export const GATE_LABEL: Record<string, string> = {
  'EVID-001': 'Párrafo sin respaldo',
  'EVID-002': 'Fuente citada sin liga',
  'EVID-003': 'Falta la atribución',
  'EVID-004': 'Sube el grado de certeza',
  'EVID-005': 'Afirma una ausencia sin acreditarla',
  'EVID-006': 'Pluralidad de fuentes inexistente',
  'EVID-007': 'Falta el contrapunto obligatorio',
  'STYLE-001': 'Oración demasiado larga',
  'STYLE-002': 'Párrafo demasiado largo',
  'STYLE-003': 'Matices apilados en una oración',
  'STYLE-004': 'Lenguaje de expediente',
};

export const READABILITY_LABEL: Record<string, string> = {
  applied: 'Editada para legibilidad',
  not_needed: 'No necesitó edición de estilo',
  not_run: 'Edición de estilo no ejecutada',
  discarded: 'Edición de estilo descartada',
  failed: 'La edición de estilo falló',
};


/**
 * Who wrote a note, in the newsroom's words.
 *
 * The routing is deliberate — ChatGPT, then Kimi, then the box — so the label is not decoration:
 * a note written locally is shorter and less sourced than the same story written by a paid model,
 * and an editor comparing two mornings has to be able to see which one they are reading.
 */
export const NOTA_PROVIDER_LABEL: Record<string, string> = {
  chatgpt: 'ChatGPT',
  kimi: 'Kimi',
  local: 'modelo local',
};

export function notaProviderLabel(provider: string | undefined, model: string): string {
  if (!provider) return model;
  return NOTA_PROVIDER_LABEL[provider] ?? provider;
}
